import PDF from './pdf'
import { capitalizeFirstLetter } from '../lib/utils'

const MARGIN_X = 14
const PAGE_WIDTH = 210
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN_X * 2

const STATEMENT_PARAGRAPH = 'This is a statement of your account as it appears in our books. ' +
  'If there are any discrepancies against your records, kindly advise us. ' +
  'Payments received after statement date are not included. ' +
  'Please affix your signature below if this statement is correct. ' +
  'Be informed that your future orders will be served upon payment of your due account.'

export default class StatementOfAccount extends PDF {
  /**
   * @param {import('jspdf').DocumentProperties} args
   */
  constructor(args = {}) {
    super()
    this.doc.setProperties({
      unit: 'mm',
      format: 'a4',
      ...args,
    })
    this.addFonts()
  }

  /**
   * @param {StatementData} data
   */
  __header({ companyName = '', logoDataUrl = '', logoFormat = 'PNG', statementDate = '' }) {
    let titleX = MARGIN_X
    if (logoDataUrl) {
      try {
        this.doc.addImage(logoDataUrl, logoFormat, MARGIN_X, 10, 24, 24)
        titleX = MARGIN_X + 28
      } catch (e) {
        titleX = MARGIN_X
      }
    }

    const title = capitalizeFirstLetter(companyName || this.getStoreName())
    this.generateText({ fontSize: 22, fontStyle: 'bold', text: title, coordinates: [titleX, 20], color: [55, 71, 79] })
    this.generateText({ fontSize: 13, text: 'Statement of Account', coordinates: [titleX, 28], color: [55, 71, 79] })

    this.doc.setDrawColor('#CFD8DC')
    this.doc.line(MARGIN_X, 37, PAGE_WIDTH - MARGIN_X, 37)

    this.generateText({ fontSize: 11, fontStyle: 'bold', text: 'Date:', coordinates: [MARGIN_X, 45] })
    this.generateText({ fontSize: 11, text: statementDate, coordinates: [MARGIN_X + 14, 45] })
  }

  /**
   * @param {StatementData} data
   */
  __recipient({ name = '', address = '' }) {
    this.generateText({ fontSize: 11, fontStyle: 'bold', text: 'Name:', coordinates: [MARGIN_X, 52] })
    this.generateText({ fontSize: 11, text: name, coordinates: [MARGIN_X + 14, 52] })

    this.generateText({ fontSize: 11, fontStyle: 'bold', text: 'Address:', coordinates: [MARGIN_X, 59] })
    if (address) {
      this.generateText({ fontSize: 11, text: address, coordinates: [MARGIN_X + 18, 59] })
    }
  }

  /**
   * A receipt's leading row keeps every column; its line items follow as
   * indented rows carrying only the item name and amount.
   *
   * @param {StatementItem} entry
   * @returns {Array[]}
   */
  __receiptRows(entry) {
    const receiptRow = [
      entry.dateDelivered,
      entry.crNumber,
      entry.dateDue,
      '',
      { content: entry.amountInvoice, styles: { halign: 'right' } },
      entry.datePaid,
      { content: entry.amountPaid, styles: { halign: 'right' } },
      { content: entry.balance, styles: { halign: 'right' } },
      { content: entry.daysOverdue, styles: { halign: 'center' } },
    ].map(cell => {
      // Shaded + bold so each receipt reads as the header of the items below it
      const styles = { fontStyle: 'bold', fillColor: [236, 239, 241] }
      return typeof cell === 'object'
        ? { ...cell, styles: { ...styles, ...cell.styles } }
        : { content: cell, styles }
    })

    const itemRows = (entry.items || []).map(item => ([
      '', '', '',
      { content: item.item, styles: { cellPadding: { top: 1.2, right: 1.2, bottom: 1.2, left: 4 } } },
      { content: item.amount, styles: { halign: 'right' } },
      '', '', '', '',
    ]))

    return [receiptRow, ...itemRows]
  }

  /**
   * @param {StatementData} data
   * @param {StatementItem[]} body
   */
  __table({ totalAmountCredit = '', totalDueAmount = '' }, body) {
    const rows = body.reduce((acc, entry) => acc.concat(this.__receiptRows(entry)), [])

    this.doc.autoTable({
      head: [[
        'Date Delivered', 'CR Number', 'Date Due', 'Item', 'Amount Invoice',
        'Date Paid', 'Amount Paid', 'Balance', 'No. of Days Overdue',
      ]],
      startY: 64,
      margin: { left: MARGIN_X, right: MARGIN_X },
      styles: { font: 'calibri', fontSize: 7, cellPadding: 1.2, overflow: 'linebreak' },
      headStyles: { fillColor: [55, 71, 79], halign: 'center', fontSize: 7 },
      // Item now carries the line-item names, so give it room before wrapping
      columnStyles: { 3: { minCellWidth: 34 } },
      body: [
        ...rows,
        [
          { content: 'Total Amount Credit', colSpan: 4, styles: { halign: 'right', fontStyle: 'bold' } },
          { content: totalAmountCredit, styles: { halign: 'right', fontStyle: 'bold' } },
          { content: '', colSpan: 4 },
        ],
        [
          { content: 'Total Due Amount', colSpan: 7, styles: { halign: 'right', fontStyle: 'bold' } },
          { content: totalDueAmount, styles: { halign: 'right', fontStyle: 'bold' } },
          { content: '', colSpan: 1 },
        ],
      ],
    })
  }

  /**
   * Ensures there is enough vertical room; adds a page if not.
   * @param {number} needed
   * @param {number} currentY
   * @returns {number}
   */
  __ensureSpace(needed, currentY) {
    const pageHeight = this.doc.internal.pageSize.height
    if (currentY + needed > pageHeight - 15) {
      this.doc.addPage()
      return 20
    }
    return currentY
  }

  /**
   * @param {StatementData} data
   */
  __footer({ companyName = '', approvedByName = '', approvedByPosition = '' }) {
    let y = (this.doc.lastAutoTable ? this.doc.lastAutoTable.finalY : 64) + 10

    // Statement paragraph (justified/wrapped)
    y = this.__ensureSpace(30, y)
    this.doc.setFont('calibri', 'normal')
    this.doc.setFontSize(10)
    this.doc.setTextColor(0, 0, 0)
    const lines = this.doc.splitTextToSize(STATEMENT_PARAGRAPH, CONTENT_WIDTH)
    this.doc.text(lines, MARGIN_X, y)
    y += lines.length * 5 + 8

    // Very truly yours + approver block
    y = this.__ensureSpace(60, y)
    this.generateText({ fontSize: 10, text: 'Very truly yours,', coordinates: [MARGIN_X, y] })
    this.generateText({ fontSize: 11, fontStyle: 'bold', text: capitalizeFirstLetter(companyName || this.getStoreName()), coordinates: [MARGIN_X, y + 7] })

    const approverY = y + 24
    this.doc.line(MARGIN_X, approverY, MARGIN_X + 70, approverY)
    if (approvedByName) {
      const nameWidth = this.getTextWidth(approvedByName, 10)
      this.generateText({ fontSize: 10, text: approvedByName, coordinates: [MARGIN_X + 35 - nameWidth / 2, approverY + 4] })
    }
    if (approvedByPosition) {
      const positionWidth = this.getTextWidth(approvedByPosition, 8)
      this.generateText({ fontSize: 8, text: approvedByPosition, coordinates: [MARGIN_X + 35 - positionWidth / 2, approverY + 8.5] })
    }

    // Credit Officer & Collection Officer (beside the approver block)
    const officerX = MARGIN_X + 100
    this.doc.line(officerX, approverY, officerX + 68, approverY)
    this.generateText({ fontSize: 8, text: 'Credit Officer', coordinates: [officerX, approverY + 4] })
    this.generateText({ fontSize: 8, text: 'Date:', coordinates: [officerX, approverY + 9] })
    this.doc.line(officerX + 10, approverY + 9, officerX + 68, approverY + 9)

    this.doc.line(officerX, approverY + 20, officerX + 68, approverY + 20)
    this.generateText({ fontSize: 8, text: 'Collection Officer', coordinates: [officerX, approverY + 24] })
    this.generateText({ fontSize: 8, text: 'Date:', coordinates: [officerX, approverY + 29] })
    this.doc.line(officerX + 10, approverY + 29, officerX + 68, approverY + 29)

    // Conformed line (recipient)
    const conformedY = approverY + 44
    this.generateText({ fontSize: 10, fontStyle: 'bold', text: 'Conformed:', coordinates: [MARGIN_X, conformedY] })
    this.doc.line(MARGIN_X + 26, conformedY, MARGIN_X + 96, conformedY)
    this.generateText({ fontSize: 8, text: 'Printed Name & Signature over Date', coordinates: [MARGIN_X + 26, conformedY + 4] })
  }

  /**
   * @param {string} name
   */
  __save(name = '') {
    const fileName = `${this.getStoreName()}_statement_of_account_${name}_${new Date().getTime()}`.replace(/[\s]+/g, '_')
    this.doc.save(fileName)
  }

  /**
   * @param {StatementData} data
   * @param {StatementItem[]} items
   */
  generate(data, items) {
    this.__header(data)
    this.__recipient(data)
    this.__table(data, items)
    this.__footer(data)
    this.__save(data.name)
  }
}

/**
 * @typedef StatementData
 * @property {string} companyName
 * @property {string} logoDataUrl
 * @property {string} logoFormat
 * @property {string} approvedByName
 * @property {string} approvedByPosition
 * @property {string} statementDate
 * @property {string} name
 * @property {string} address
 * @property {string} totalAmountCredit
 * @property {string} totalDueAmount
 *
 * @typedef StatementLineItem
 * @property {string} item
 * @property {string} amount
 *
 * @typedef StatementItem
 * @property {string} dateDelivered
 * @property {string} crNumber
 * @property {string} dateDue
 * @property {string} amountInvoice
 * @property {string} datePaid
 * @property {string} amountPaid
 * @property {string} balance
 * @property {string|number} daysOverdue
 * @property {StatementLineItem[]} items
 */
